import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { useAuth } from './AuthContext';

const MailFoldersContext = createContext(null);

/** Flache Liste → Baum mit children[] */
export const buildFolderTree = (categories = []) => {
  const nodes = new Map();
  (categories || []).forEach((c) => {
    nodes.set(String(c.id), { ...c, children: [] });
  });
  const roots = [];
  nodes.forEach((node) => {
    const parentKey = node.parentId != null ? String(node.parentId) : null;
    if (parentKey && nodes.has(parentKey)) {
      nodes.get(parentKey).children.push(node);
    } else {
      roots.push(node);
    }
  });
  const sortRec = (list) => {
    list.sort((a, b) => {
      const so = (Number(a.sortOrder) || 0) - (Number(b.sortOrder) || 0);
      if (so !== 0) return so;
      return String(a.name || '').localeCompare(String(b.name || ''), 'de');
    });
    list.forEach((n) => sortRec(n.children));
  };
  sortRec(roots);
  return roots;
};

/** Baum → flache Liste mit depth für UI */
export const flattenFolderTree = (tree = [], depth = 0, out = []) => {
  tree.forEach((node) => {
    out.push({ ...node, depth });
    if (node.children?.length) flattenFolderTree(node.children, depth + 1, out);
  });
  return out;
};

export const MailFoldersProvider = ({ children }) => {
  const { user, isAuthenticated } = useAuth();
  const userId = user?.id ?? null;
  const [categories, setCategories] = useState([]);
  const [spamRules, setSpamRules] = useState({ enabled: true, rules: [] });
  const [loading, setLoading] = useState(true);

  const refreshCategories = useCallback(async () => {
    if (!isAuthenticated) {
      setCategories([]);
      return;
    }
    try {
      const res = await axios.get('/api/mail-categories');
      setCategories(Array.isArray(res.data?.categories) ? res.data.categories : []);
    } catch (err) {
      console.error('mail-categories laden fehlgeschlagen:', err?.response?.data || err.message || err);
      // Bestehende Liste behalten — nicht leeren (sonst „verschwinden“ Ordner nach Create)
    }
  }, [isAuthenticated]);

  const refreshSpamRules = useCallback(async () => {
    if (!isAuthenticated) {
      setSpamRules({ enabled: true, rules: [] });
      return;
    }
    try {
      const res = await axios.get('/api/spam-rules');
      setSpamRules({
        enabled: res.data?.enabled !== false,
        rules: Array.isArray(res.data?.rules) ? res.data.rules : [],
      });
    } catch (_) {
      /* ignore */
    }
  }, [isAuthenticated]);

  const refreshAll = useCallback(async () => {
    setLoading(true);
    await Promise.all([refreshCategories(), refreshSpamRules()]);
    setLoading(false);
  }, [refreshCategories, refreshSpamRules]);

  useEffect(() => {
    if (!isAuthenticated || !userId) {
      setCategories([]);
      setSpamRules({ enabled: true, rules: [] });
      setLoading(false);
      return;
    }
    refreshAll();
  }, [isAuthenticated, userId, refreshAll]);

  const createCategory = useCallback(async (name, color = '#0f766e', parentId = null) => {
    const res = await axios.post('/api/mail-categories', {
      name,
      color,
      parentId: parentId == null ? null : parentId,
    });
    const created = res.data?.category || null;
    if (created?.id != null) {
      setCategories((prev) => {
        const exists = prev.some((c) => String(c.id) === String(created.id));
        if (exists) return prev;
        return [
          ...prev,
          {
            id: created.id,
            name: created.name || name,
            color: created.color || color,
            parentId: created.parentId != null ? created.parentId : parentId,
            sortOrder: Number(created.sortOrder) || 0,
            itemCount: Number(created.itemCount) || 0,
          },
        ];
      });
    }
    await refreshCategories();
    return created;
  }, [refreshCategories]);

  const deleteCategory = useCallback(async (id) => {
    await axios.delete(`/api/mail-categories/${id}`);
    await refreshCategories();
  }, [refreshCategories]);

  const updateCategory = useCallback(async (id, patch = {}) => {
    const payload = {};
    if (patch.name != null) payload.name = patch.name;
    if (patch.color != null) payload.color = patch.color;
    if (Object.prototype.hasOwnProperty.call(patch, 'parentId')) {
      payload.parentId = patch.parentId;
    }
    if (patch.sortOrder != null) payload.sortOrder = patch.sortOrder;
    await axios.put(`/api/mail-categories/${id}`, payload);
    await refreshCategories();
  }, [refreshCategories]);

  const saveSpamRules = useCallback(async (payload) => {
    const res = await axios.put('/api/spam-rules', payload);
    setSpamRules({
      enabled: res.data?.enabled !== false,
      rules: Array.isArray(res.data?.rules) ? res.data.rules : [],
    });
    return res.data;
  }, []);

  const resetSpamRulesDefaults = useCallback(async ({ keepCustom = true } = {}) => {
    const res = await axios.post('/api/spam-rules/reset-defaults', { keepCustom });
    setSpamRules({
      enabled: res.data?.enabled !== false,
      rules: Array.isArray(res.data?.rules) ? res.data.rules : [],
    });
    return res.data;
  }, []);

  const folderTree = useMemo(() => buildFolderTree(categories), [categories]);
  const flatFolders = useMemo(() => flattenFolderTree(folderTree), [folderTree]);

  const value = useMemo(
    () => ({
      categories,
      folderTree,
      flatFolders,
      spamRules,
      loading,
      refreshCategories,
      refreshSpamRules,
      refreshAll,
      createCategory,
      updateCategory,
      deleteCategory,
      saveSpamRules,
      resetSpamRulesDefaults,
    }),
    [
      categories,
      folderTree,
      flatFolders,
      spamRules,
      loading,
      refreshCategories,
      refreshSpamRules,
      refreshAll,
      createCategory,
      updateCategory,
      deleteCategory,
      saveSpamRules,
      resetSpamRulesDefaults,
    ]
  );

  return (
    <MailFoldersContext.Provider value={value}>
      {children}
    </MailFoldersContext.Provider>
  );
};

export const useMailFolders = () => {
  const ctx = useContext(MailFoldersContext);
  if (!ctx) {
    throw new Error('useMailFolders must be used within MailFoldersProvider');
  }
  return ctx;
};
